import { OutputDesignation, PortfolioType } from '@ddt-web/config';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as partition from 'lodash/partition';

import { ComponentData } from '../models/component-data';
import { CellRow } from '../models/interfaces/component.interface';
import { TabData } from '../models/tab-data';

export class PortfolioDataUtil {

	public static getBenchmarkPortfolioName(portfolios: Array<CellRow>): string {
		const benchmarkPortfolio = portfolios.find(portfolio => (get(portfolio, 'field.isBenchmark.rawValue') === true)
			|| (get(portfolio, 'field.outputDesignation.rawValue') === OutputDesignation.BENCHMARK));
		const benchmarkPortfolioName = get(benchmarkPortfolio, 'field.name.formattedValue', '');
		return benchmarkPortfolioName;
	}

	public static getIndexOfBenchmarkPortfolio(portfolioData: Array<CellRow>, benchmarkPortfolioName: string): number {
		return portfolioData.findIndex(portfolio => {
			const nameIndex = portfolio.findIndex(cell => get(cell, 'config.id') === 'name');
			return get(portfolio, `${nameIndex}.formattedValue`) === benchmarkPortfolioName;
		});
	}

	/**
	 * Group the data by portfolio tab and benchmark tab
	 *
	 * @static
	 * @param {Array<TabData>} tabsData
	 * @param {string} filterKeyForBenchmarkTabs
	 * @param {Array<string>} [portfolioTabsOverrideKeys]
	 * @return {*}
	 * @memberof PortfolioDataUtil
	 */
	public static getTabsDataByCategory(tabsData: Array<TabData>, filterKeyForBenchmarkTabs: string, portfolioTabsOverrideKeys?: Array<string>): Array<{ [key: string]: TabData }> {
		const tabsByViewCategory = [];

		// divide the tabs into two arrays : portfolio and benchmark tabs
		const [benchmarkTabs, portfolioTabs] = filterKeyForBenchmarkTabs ? partition(tabsData, tab => get(tab, 'config.id').includes(filterKeyForBenchmarkTabs)) : [[], tabsData];
		portfolioTabs.forEach(tabData => {

			const tabId = get(tabData, 'config.id');

			// if there is a matching portfolio tabId or substring of portfolio tabId present in benchmark tab, then  attach benchmark tab to the matching portfolio tab
			const benchmarkTabIdentifier = !isEmpty(portfolioTabsOverrideKeys) && tabId.includes(portfolioTabsOverrideKeys) ?
				portfolioTabsOverrideKeys.find(key => tabId.includes(key)) : tabId;

			let tabItem = { portfolio: tabData };

			// find if there is benchmark tab for the respective portfolio tab based on benchmarkTabIdentifier
			const benchmarkTabData = benchmarkTabs.find(tab => get(tab, 'config.id').includes(benchmarkTabIdentifier));
			if (benchmarkTabData) {
				tabItem = Object.assign({}, tabItem, { benchmark: benchmarkTabData });
			}
			tabsByViewCategory.push(tabItem);
		});
		return tabsByViewCategory;
	}

	public static hasBenchmarkPortfolio(portfolioData: CellRow[]): boolean {
		const hasBenchmark = !isEmpty(portfolioData) ? portfolioData.some(row => get(row, 'field.outputDesignation.rawValue') === OutputDesignation.BENCHMARK) : false;
		return hasBenchmark;
	}

	public static isUserPortfolio(portfolio: CellRow): boolean {
		return get(portfolio, 'field[portfolioType].formattedValue') === PortfolioType.PORTFOLIO;
	}

	public static isModelPortfolio(portfolio: CellRow): boolean {
		return get(portfolio, 'field[portfolioType].formattedValue') === PortfolioType.MODEL;
	}

	public static isThirdPartyPortfolio(portfolio: CellRow): boolean {
		return get(portfolio, 'field[portfolioType].formattedValue') === PortfolioType.THIRD_PARTY;
	}

	public static isBenchmarkPortfolio(portfolio: CellRow): boolean {
		return portfolio?.field?.outputDesignation?.rawValue === OutputDesignation.BENCHMARK || portfolio?.field?.isBenchmark?.rawValue === true;
	}

	/**
	 * Returns true if portfolio is userPortfolio and not selected as benchmark
	 * In all other cases returns false
	 * @param {CellRow} portfolio
	 * @returns {boolean}
	 */
	public static isValidPortfolioToGenerateInsights(portfolio: CellRow): boolean {
		return this.isUserPortfolio(portfolio) && !this.isBenchmarkPortfolio(portfolio);
	}
	/**
	 * Returns index of first portfolio which is a user-portfolio and not selected as portfolio
	 * If there doesn't exists such a portfolio, returns -1
	 * @param data
	 * @returns {number}
	 */
	public static getFirstValidUserPortfolio(data: ComponentData): number {
		return data?.portfolioData?.findIndex(portfolio => this.isValidPortfolioToGenerateInsights(portfolio));
	}
}
